import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QuoteForm = ({ title = "Request a Free Quote" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    projectType: "",
    message: ""
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('projectType', formData.projectType);
      if (formData.message) {
        formDataToSend.append('message', formData.message);
      }
      
      // Append photos if any
      photos.forEach((photo) => {
        formDataToSend.append('photos', photo);
      });

      const response = await axios.post(
        `${BACKEND_URL}/api/quote-requests`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success("Quote Request Submitted!", {
          description: "We'll contact you within 24 hours to discuss your project.",
          duration: 5000,
        });
        
        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          location: "",
          projectType: "",
          message: ""
        });
        setPhotos([]);
        
        // Reset file input
        const fileInput = document.getElementById('photos');
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error("Submission Failed", {
        description: "Please try again or call us at 07846 923387",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos(files.slice(0, 5)); // Limit to 5 photos
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8">
      <h3 className="text-2xl font-light text-white mb-6">{title}</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label htmlFor="name" className="text-neutral-300">Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-2 bg-neutral-800 border-neutral-700 text-white"
            placeholder="Your full name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="email" className="text-neutral-300">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-2 bg-neutral-800 border-neutral-700 text-white"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-neutral-300">Phone *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className="mt-2 bg-neutral-800 border-neutral-700 text-white"
              placeholder="07XXX XXXXXX"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="location" className="text-neutral-300">Location *</Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              className="mt-2 bg-neutral-800 border-neutral-700 text-white"
              placeholder="e.g., Kensington, London"
            />
          </div>
          <div>
            <Label htmlFor="projectType" className="text-neutral-300">Project Type *</Label>
            <Select
              value={formData.projectType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, projectType: value }))}
              required
            >
              <SelectTrigger className="mt-2 bg-neutral-800 border-neutral-700 text-white">
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Pergola Installation">Pergola Installation</SelectItem>
                <SelectItem value="Motorised Pergola">Motorised Pergola</SelectItem>
                <SelectItem value="Pergola Blinds">Pergola Blinds</SelectItem>
                <SelectItem value="Glass Veranda">Glass Veranda</SelectItem>
                <SelectItem value="Rooflight Blinds">Rooflight Blinds</SelectItem>
                <SelectItem value="Shutters">Plantation Shutters</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="message" className="text-neutral-300">Project Details</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="mt-2 bg-neutral-800 border-neutral-700 text-white"
            placeholder="Tell us about your outdoor living project..."
          />
        </div>

        <div>
          <Label htmlFor="photos" className="text-neutral-300">Upload Garden Photos (Optional)</Label>
          <div className="mt-2 relative">
            <input
              id="photos"
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            <label
              htmlFor="photos"
              className="flex flex-col items-center justify-center border-2 border-dashed border-neutral-700 rounded-lg p-6 cursor-pointer hover:border-neutral-600 transition-colors"
            >
              <Upload className="h-8 w-8 text-neutral-500 mb-2" />
              <p className="text-sm text-neutral-400">Click to upload or drag and drop</p>
              <p className="text-xs text-neutral-500 mt-1">PNG, JPG up to 10MB (max 5 photos)</p>
              {photos.length > 0 && (
                <p className="text-sm text-white mt-2">{photos.length} photo(s) selected</p>
              )}
            </label>
          </div>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting || !formData.projectType}
          className="w-full bg-white text-neutral-900 hover:bg-neutral-100 transition-all duration-200 py-6 disabled:opacity-50"
        >
          {isSubmitting ? "Submitting..." : "Request Free Quote"}
        </Button>
      </form>
    </div>
  );
};
